// Claims to review — UI-only types (CLAIMS_UI_BUILD_SPEC §14.2).
// No backend: every value here is produced by the in-memory mock store.

export type Mode = 'training' | 'autopilot';

export type Origin =
  | 'added by ELVA'
  | `from ${string}`
  | `added by ${string}`
  | `chosen by ${string}`;

export interface Procedure {
  id: string;
  code: string;
  description: string;
  tooth: string | null;
  fee: number;
  elvaTouched?: boolean;
}

export type AttachmentKind = 'xray' | 'photo' | 'document';

export interface Attachment {
  id: string;
  name: string;
  kind: AttachmentKind;
  date: string;
  origin: Origin;
  /** Object URL for a file the user added this session (never uploaded). */
  objectUrl?: string;
  /** True for attachments the signed-in user added this session (removable with ×). */
  userAdded?: boolean;
}

/** A reversible description of what an ELVA change set, and what it was before. */
export interface Patch {
  /** Per-procedure field overrides, matched by procedure id. */
  procedures?: {
    id: string;
    code?: string;
    description?: string;
    tooth?: string | null;
    fee?: number;
    elvaTouched?: boolean;
  }[];
  /** Set the narrative text. Empty string clears it. */
  narrativeText?: string;
  /** Add this attachment. */
  addAttachment?: Attachment;
  /** Remove the attachment with this id. */
  removeAttachmentId?: string;
  /** Replace the payer. */
  payer?: { name: string; short: string };
  /** Set the remarks field. */
  remarks?: string;
}

export interface Change {
  id: string;
  title: string;
  why: string;
  evidenceId?: string;
  evidenceLabel?: string;
  applied: boolean;
  revert: { before: Patch; after: Patch };
  undoWarning?: string;
}

/** `dont_bill` is a money action (D245): hidden for a user without the permission. */
export type DecisionEffect = 'attach' | 'none' | 'hold' | 'edit_narrative' | 'dont_bill';

export interface DecisionOption {
  id: string;
  label: string;
  style: 'secondary' | 'ghost';
  effect: DecisionEffect;
  attachment?: Attachment;
  holdReason?: string;
  holdNote?: string;
  narrative?: string;
  resolvedText: string;
}

export interface Decision {
  title: string;
  body: string;
  options: DecisionOption[];
  evidence: { id: string; label: string }[];
  /** Id of the chosen option once resolved. */
  chosen?: string;
}

export interface Narrative {
  code: string;
  text: string;
  basis: string;
  alt?: string;
  /** How the current text came to be — drives the origin line (§6.6). */
  state?: 'elva' | 'edited' | 'rewritten' | 'empty';
}

export type ClaimSource =
  | { kind: 'pms_draft'; createdAt: string; reviewedAt: string }
  | { kind: 'elva_created'; note: string; reviewedAt: string };

export interface Held {
  reason: string;
  note?: string;
  until?: string;
}

/** The payer's timely-filing limit for a claim (D237). `confirmed: false` = the payer's limit isn't researched. */
export interface TimelyFiling {
  limitIso: string;
  confirmed: boolean;
}

/** The hold reason ELVA uses when it added a clinical finding that isn't written in the chart (D176). */
export const DENTIST_APPROVAL_HOLD = 'Needs Dr. Lee’s approval';

/**
 * A clinical finding ELVA added that the chart doesn't record, e.g. read from an X-ray. The
 * dentist approves it on screen by typing his name (D176). Not a question: a hold reason.
 */
export interface FindingApproval {
  /** "Dr. Marcus Lee" */
  dentist: string;
  /** What ELVA added, quoted. */
  finding: string;
  /** Where ELVA read it: "the periapical X-ray of Oct 15". */
  source: string;
  status: 'needed' | 'asked' | 'approved';
  askedAt?: string;
  approvedAt?: string;
}

export interface LearningOverride {
  did: string;
  will: string;
  scope: string;
}

export interface Claim {
  id: string;
  patient: string;
  memberId: string;
  payer: { name: string; short: string };
  serviceDate: string;
  provider: string;
  source: ClaimSource;
  usuallyPays: number | null;
  procedureGroup: string;
  procedures: Procedure[];
  decision?: Decision;
  changes: Change[];
  narrative?: Narrative;
  attachments: Attachment[];
  remarks: string;
  checkedAgainst?: { sentence: string; label: string; evidenceId: string };
  listLine: string;
  held?: Held;
  /** Payer's filing limit; within 14 days of it the row says "File by Nov 4 · 9 days". */
  timelyFiling?: TimelyFiling;
  /** Set when ELVA added a finding the chart doesn't have; the hold reason is DENTIST_APPROVAL_HOLD. */
  findingApproval?: FindingApproval;
  heldReason?: string; // autopilot only
  heldShort?: string; // autopilot only
  learningOverride?: LearningOverride;
  history: string[];
  /**
   * Whether the patient's PMS record has an assignment of benefits for this
   * payer. Absent = on file. `'none'` + a dentist out of network with the payer
   * means the payer will pay the patient (D197); the review pane says so.
   */
  assignmentOfBenefits?: 'on_file' | 'none';
  /** Set when the claim was confirmed / not billed this session (kept for deep links). */
  removed?: 'confirmed' | 'not_billed';
}

export type QueueGroup = 'needs_decision' | 'ready' | 'on_hold' | 'waiting';
export type RowTag = 'on_hold' | 'needs_you' | 'elva_created' | 'elva_changed' | 'no_changes';

/** The timely-filing countdown on a row: "File by Nov 4 · 9 days" (· not confirmed). */
export interface FileBy {
  days: number;
  label: string;
}

export interface ClaimSummary {
  id: string;
  patient: string;
  memberId: string;
  payerShort: string;
  codes: string[];
  billed: number;
  serviceDate: string;
  tag: RowTag;
  group: QueueGroup;
  listLine: string;
  fileBy?: FileBy;
}

export interface EvidenceRow {
  claim: string;
  date: string;
  outcome: string;
}

export interface Evidence {
  id: string;
  title: string;
  summary: string;
  table?: EvidenceRow[];
  /** When set, the link opens the attachment viewer rather than the popover. */
  attachmentId?: string;
}

export interface ClaimEditPatch {
  procedures?: Procedure[];
  narrativeText?: string;
  remarks?: string;
  attachments?: Attachment[];
}

export interface Settings {
  mode: Mode;
  confirmed: number;
  milestoneShown: boolean;
  autopilotSince?: string;
  autopilotBy?: string;
}

export interface ConfirmResult {
  ok: boolean;
  confirmed?: number;
  error?: 'writeback_failed';
}
